/**
 * Zod schema for "Add Snyk Connection" (Charter M3 migration), imported by
 * `SnykConnectionForm.tsx`, never redefined (form-standard.context.md §3). Rule IDs `VSK-*` mirror
 * the pre-migration hand-rolled `validate()` exactly — including that the UUID-format check only
 * fires when the field is non-empty (empty always shows the "required" message, never both).
 */
import { z } from 'zod';
import type { CreateSnykConnectionRequest } from '../types';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const SnykConnectionSchema = z.object({
  // VSK-01
  name: z.string().trim().min(1, 'Connection name is required'),
  // VSK-02 (required) / VSK-03 (must be UUID-shaped)
  organizationId: z
    .string()
    .trim()
    .min(1, 'Organization ID is required')
    .refine((v) => UUID_REGEX.test(v), {
      message: 'Organization ID should be a UUID (e.g., 12345678-abcd-1234-abcd-1234567890ab)',
    }),
  // VSK-04
  apiKey: z.string().trim().min(1, 'API Key is required'),
});

/** The shape RHF's `useForm<T>`/`defaultValues` are typed against (pre-transform). */
export type SnykConnectionFormValues = z.input<typeof SnykConnectionSchema>;
/** The shape `handleSubmit(onValid)` receives (post-transform — trimmed). */
export type SnykConnectionFormOutput = z.output<typeof SnykConnectionSchema>;

// Compile-time proof the schema's output assembles a valid request DTO.
type _AssertAssignable = SnykConnectionFormOutput extends CreateSnykConnectionRequest ? true : false;
const _assertAssignable: _AssertAssignable = true;
void _assertAssignable;
